import { GameState } from '../game/GameState';
import type {
  CharacterGender,
  CharacterRole,
  CharacterState,
  CourtEstateId,
  CourtEstateState,
  CourtIncidentState,
  EstateDemandState,
  FactionState,
  SuccessionLaw,
} from '../types/game';
import { DataRegistry } from './DataRegistry';

const ESTATES: Array<{ id: CourtEstateId; name: string }> = [
  { id: 'nobility', name: 'Noble Houses' },
  { id: 'clergy', name: 'Clergy and Temples' },
  { id: 'merchants', name: 'Merchant Guilds' },
  { id: 'military', name: 'Military Retainers' },
  { id: 'commoners', name: 'Village Communities' },
];

const SUCCESSION_LABELS: Record<SuccessionLaw, string> = {
  agnatic_primogeniture: 'Agnatic Primogeniture',
  absolute_primogeniture: 'Absolute Primogeniture',
  seniority: 'Dynastic Seniority',
  elective_court: 'Court Election',
  designated_heir: 'Designated Heir',
};

const TRAITS = [
  'ambitious', 'patient', 'just', 'gregarious', 'scholar', 'zealous', 'brave', 'cautious',
  'diplomatic', 'administrator', 'tactician', 'intriguer', 'generous', 'traditionalist',
];

const GIVEN_NAMES: Record<string, string[]> = {
  majapahit: ['Hayam', 'Gajah', 'Wikramawardhana', 'Suhita', 'Kertawijaya', 'Ratnapangkaja', 'Dyah'],
  sunda: ['Niskala', 'Surawisesa', 'Jayadewata', 'Ratu', 'Prabu', 'Dewata', 'Mayangsari'],
  demak: ['Raden', 'Patah', 'Trenggana', 'Prawata', 'Kalinyamat', 'Hadiri', 'Sekar'],
  cirebon: ['Syarif', 'Gunung', 'Jati', 'Hasanuddin', 'Pakungwati', 'Wangsakerta', 'Rara'],
  banten: ['Maulana', 'Hasanuddin', 'Yusuf', 'Muhammad', 'Ratu', 'Fatimah', 'Khadijah'],
  mataram: ['Panembahan', 'Senapati', 'Hanyakrawati', 'Agung', 'Purbaya', 'Ratu', 'Sekar'],
  blambangan: ['Menak', 'Jingga', 'Sembuyu', 'Tawang', 'Wijaya', 'Puspa', 'Kenanga'],
  bali: ['Dalem', 'Waturenggong', 'Baturenggong', 'Pemayun', 'Dewa', 'Anak', 'Ayu'],
  madura: ['Cakraningrat', 'Arya', 'Pratanu', 'Lembu', 'Raden', 'Sari', 'Ratih'],
  malacca: ['Mansur', 'Mahmud', 'Alauddin', 'Muzaffar', 'Hang', 'Tun', 'Puteri'],
  aceh: ['Ali', 'Mughayat', 'Salahuddin', 'Alauddin', 'Iskandar', 'Safiatuddin', 'Nurul'],
  portuguese: ['Joao', 'Afonso', 'Manuel', 'Francisco', 'Duarte', 'Isabel', 'Beatriz'],
  dutch_voc: ['Jan', 'Pieter', 'Cornelis', 'Hendrik', 'Willem', 'Anna', 'Margaretha'],
  british_eic: ['Thomas', 'Edward', 'William', 'Henry', 'Richard', 'Elizabeth', 'Catherine'],
  ming: ['Zhu', 'Li', 'Wang', 'Chen', 'Zhang', 'Mei', 'Lan'],
  arab_confederation: ['Ahmad', 'Yusuf', 'Omar', 'Hasan', 'Khalid', 'Aisha', 'Maryam'],
};

export const successionLaws = Object.entries(SUCCESSION_LABELS).map(([id, label]) => ({ id: id as SuccessionLaw, label }));

export class DynastyManager {
  static readonly instance = new DynastyManager();

  ensureCampaignInitialized() {
    const campaign = GameState.instance.requireCampaign();
    campaign.characters ??= {};
    campaign.courtEstates ??= {};
    campaign.estateDemands ??= {};
    campaign.courtIncidents ??= {};
    campaign.dynastyHistory ??= [];

    for (const faction of Object.values(campaign.factions)) {
      faction.dynasty ??= {
        house: `${DataRegistry.instance.factions[faction.id]?.name ?? faction.id} Court`,
        successionClarity: 55,
        successionLaw: 'absolute_primogeniture',
        regency: false,
      };
      faction.dynasty.successionLaw ??= 'absolute_primogeniture';
      faction.dynasty.regency ??= false;
      this.ensureInitialFamily(faction);
      this.ensureEstates(faction.id);
    }
    this.syncOfficeHolders();

    const playerDynasty = campaign.factions[campaign.playerFactionId]?.dynasty;
    campaign.selectedCharacterId ??= playerDynasty?.rulerId;
  }

  processMonth() {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();

    if (campaign.month === 12) {
      for (const character of Object.values(campaign.characters)) {
        if (!character.alive) continue;
        character.age += 1;
        character.health = Math.max(0, Math.min(100, character.health + this.healthDrift(character)));
        if (this.shouldDie(character, campaign.turn)) this.killCharacter(character.id, this.deathCause(character));
      }
      for (const faction of Object.values(campaign.factions)) this.considerBirth(faction.id);
    }

    for (const faction of Object.values(campaign.factions)) {
      this.processRegency(faction.id);
      this.processEstates(faction.id);
      this.processExpiredDemands(faction.id);
      this.considerCourtCrisis(faction.id);
    }
  }

  courtForFaction(factionId: string) {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const dynasty = faction.dynasty!;
    return {
      house: dynasty.house,
      legitimacy: faction.legitimacy,
      prestige: faction.prestige,
      successionClarity: dynasty.successionClarity,
      successionLaw: dynasty.successionLaw,
      regency: dynasty.regency,
      ruler: dynasty.rulerId ? campaign.characters[dynasty.rulerId] : undefined,
      heir: dynasty.heirId ? campaign.characters[dynasty.heirId] : undefined,
      consort: dynasty.consortId ? campaign.characters[dynasty.consortId] : undefined,
      regent: dynasty.regentId ? campaign.characters[dynasty.regentId] : undefined,
    };
  }

  familyForFaction(factionId: string) {
    this.ensureCampaignInitialized();
    return Object.values(GameState.instance.requireCampaign().characters)
      .filter((character) => character.factionId === factionId && character.house === GameState.instance.requireCampaign().factions[factionId].dynasty?.house)
      .sort((a, b) => Number(b.alive) - Number(a.alive) || this.roleOrder(a.role) - this.roleOrder(b.role) || b.claimStrength - a.claimStrength || a.age - b.age);
  }

  courtiersForFaction(factionId: string) {
    this.ensureCampaignInitialized();
    return Object.values(GameState.instance.requireCampaign().characters)
      .filter((character) => character.factionId === factionId && character.alive)
      .sort((a, b) => b.influence - a.influence || b.loyalty - a.loyalty);
  }

  estatesForFaction(factionId: string) {
    this.ensureCampaignInitialized();
    return ESTATES.map((estate) => GameState.instance.requireCampaign().courtEstates[this.estateKey(factionId, estate.id)]).filter(Boolean);
  }

  incidentsForFaction(factionId: string) {
    this.ensureCampaignInitialized();
    return Object.values(GameState.instance.requireCampaign().courtIncidents)
      .filter((incident) => incident.factionId === factionId && !incident.resolved)
      .sort((a, b) => b.severity - a.severity || b.createdTurn - a.createdTurn);
  }

  demandsForFaction(factionId: string) {
    this.ensureCampaignInitialized();
    const estateIds = new Set(this.estatesForFaction(factionId).map((estate) => estate.id));
    return Object.values(GameState.instance.requireCampaign().estateDemands)
      .filter((demand) => estateIds.has(demand.estateId) && !demand.resolved)
      .sort((a, b) => a.dueTurn - b.dueTurn);
  }

  holdCourt(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (faction.treasury < 45) {
      GameState.instance.log('Holding court requires 45 gold.', 'warning');
      return false;
    }
    faction.treasury -= 45;
    faction.legitimacy = Math.min(100, faction.legitimacy + 5);
    faction.prestige = Math.min(100, faction.prestige + 3);
    faction.dynasty!.successionClarity = Math.min(100, faction.dynasty!.successionClarity + 4);
    for (const estate of this.estatesForFaction(factionId)) estate.approval = Math.min(100, estate.approval + 3);
    const ruler = faction.dynasty?.rulerId ? campaign.characters[faction.dynasty.rulerId] : undefined;
    if (ruler) ruler.influence = Math.min(100, ruler.influence + 2);
    this.recordHistory(factionId, 'coronation', 'The Great Audience', 'The ruler received petitions, confirmed privileges and displayed the strength of the dynasty.', ruler ? [ruler.id] : []);
    GameState.instance.log('Court ceremony strengthened the dynasty and calmed the estates.', 'success');
    return true;
  }

  changeSuccessionLaw(factionId: string, law: SuccessionLaw) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction?.dynasty || faction.treasury < 75 || faction.legitimacy < 45) return false;
    faction.treasury -= 75;
    faction.legitimacy = Math.max(0, faction.legitimacy - 8);
    faction.dynasty.successionLaw = law;
    faction.dynasty.successionClarity = Math.max(25, faction.dynasty.successionClarity - 12);
    for (const estate of this.estatesForFaction(factionId)) estate.approval = Math.max(0, estate.approval - (estate.id === 'nobility' ? 10 : 3));
    this.recalculateHeir(factionId);
    this.recordHistory(factionId, 'succession_crisis', 'Succession Law Reformed', `${SUCCESSION_LABELS[law]} became the law of the realm.`, faction.dynasty.heirId ? [faction.dynasty.heirId] : []);
    GameState.instance.log(`Succession law changed to ${SUCCESSION_LABELS[law]}.`, 'warning');
    return true;
  }

  designateHeir(factionId: string, characterId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const candidate = campaign.characters[characterId];
    if (!faction?.dynasty || !candidate?.alive || candidate.factionId !== factionId || candidate.house !== faction.dynasty.house) return false;
    if (faction.dynasty.successionLaw !== 'designated_heir') return false;
    faction.dynasty.heirId = candidate.id;
    candidate.role = 'heir';
    candidate.claimStrength = Math.max(candidate.claimStrength, 80);
    faction.dynasty.successionClarity = Math.min(100, faction.dynasty.successionClarity + 8);
    GameState.instance.log(`${candidate.name} was publicly designated as heir.`, 'success');
    return true;
  }

  arrangeMarriage(factionId: string, characterId: string, partnerId: string) {
    const campaign = GameState.instance.requireCampaign();
    const first = campaign.characters[characterId];
    const second = campaign.characters[partnerId];
    if (!first?.alive || !second?.alive || first.spouseId || second.spouseId || first.id === second.id) return false;
    if (first.age < 16 || second.age < 16 || first.gender === second.gender) return false;
    first.spouseId = second.id;
    second.spouseId = first.id;
    first.relationships[second.id] = 45;
    second.relationships[first.id] = 45;
    first.factionId = factionId;
    second.factionId = factionId;
    if (first.role === 'ruler') { second.role = 'consort'; campaign.factions[factionId].dynasty!.consortId = second.id; }
    if (second.role === 'ruler') { first.role = 'consort'; campaign.factions[factionId].dynasty!.consortId = first.id; }
    campaign.factions[factionId].prestige = Math.min(100, campaign.factions[factionId].prestige + 4);
    this.recordHistory(factionId, 'marriage', 'Dynastic Marriage', `${first.name} and ${second.name} were married before the assembled court.`, [first.id, second.id]);
    this.addIncident(factionId, 'marriage', 'Dynastic Marriage', `${first.name} and ${second.name} have joined their houses.`, 'event_court_marriage', [first.id, second.id], 2);
    return true;
  }

  respondToDemand(factionId: string, demandId: string, accept: boolean) {
    const campaign = GameState.instance.requireCampaign();
    const demand = campaign.estateDemands[demandId];
    const estate = demand ? campaign.courtEstates[this.estateKey(factionId, demand.estateId)] : undefined;
    const faction = campaign.factions[factionId];
    if (!demand || !estate || demand.resolved || !faction) return false;
    demand.resolved = true;
    demand.accepted = accept;
    delete estate.activeDemandId;
    if (accept) {
      const cost = Math.min(faction.treasury, demand.treasuryCost ?? 0);
      faction.treasury -= cost;
      faction.legitimacy = Math.max(0, faction.legitimacy - (demand.legitimacyCost ?? 0));
      estate.approval = Math.min(100, estate.approval + demand.rewardApproval);
      GameState.instance.log(`${estate.name} welcomed the court's concession.`, 'success');
    } else {
      estate.approval = Math.max(0, estate.approval - demand.penaltyApproval);
      estate.power = Math.min(100, estate.power + 4);
      GameState.instance.log(`${estate.name} resented the refusal.`, 'warning');
    }
    return true;
  }

  resolveIncident(factionId: string, incidentId: string, conciliatory: boolean) {
    const campaign = GameState.instance.requireCampaign();
    const incident = campaign.courtIncidents[incidentId];
    const faction = campaign.factions[factionId];
    if (!incident || incident.factionId !== factionId || incident.resolved || !faction) return false;
    incident.resolved = true;
    if (conciliatory) {
      const cost = Math.min(faction.treasury, 25 + incident.severity * 10);
      faction.treasury -= cost;
      faction.legitimacy = Math.min(100, faction.legitimacy + 2);
      for (const estate of this.estatesForFaction(factionId)) estate.approval = Math.min(100, estate.approval + 2);
    } else {
      faction.prestige = Math.min(100, faction.prestige + 2);
      faction.legitimacy = Math.max(0, faction.legitimacy - incident.severity * 2);
      for (const estate of this.estatesForFaction(factionId)) if (estate.approval < 50) estate.approval = Math.max(0, estate.approval - 3);
    }
    return true;
  }

  private ensureInitialFamily(faction: FactionState) {
    const campaign = GameState.instance.requireCampaign();
    const dynasty = faction.dynasty!;
    if (dynasty.rulerId && campaign.characters[dynasty.rulerId]) return;
    const seed = this.hash(`${faction.id}:${campaign.scenarioId}:${campaign.year}`);
    const rulerGender: CharacterGender = seed % 7 === 0 ? 'female' : 'male';
    const ruler = this.createCharacter(faction.id, 'ruler', rulerGender, 37 + (seed % 18), 88, 90, 86, 100, 2);
    const consort = this.createCharacter(faction.id, 'consort', rulerGender === 'male' ? 'female' : 'male', 29 + ((seed >> 2) % 15), 82, 75, 62, 35, 3);
    const heirGender: CharacterGender = dynasty.successionLaw === 'agnatic_primogeniture' ? 'male' : ((seed >> 3) % 2 ? 'female' : 'male');
    const heir = this.createCharacter(faction.id, 'heir', heirGender, 15 + ((seed >> 5) % 12), 90, 70, 55, 78, 4);
    ruler.spouseId = consort.id; consort.spouseId = ruler.id;
    ruler.childIds.push(heir.id); consort.childIds.push(heir.id); heir.parentIds = [ruler.id, consort.id];
    ruler.relationships[consort.id] = 38; consort.relationships[ruler.id] = 38;
    ruler.relationships[heir.id] = 50; consort.relationships[heir.id] = 55; heir.relationships[ruler.id] = 45; heir.relationships[consort.id] = 50;
    campaign.characters[ruler.id] = ruler;
    campaign.characters[consort.id] = consort;
    campaign.characters[heir.id] = heir;
    dynasty.rulerId = ruler.id;
    dynasty.consortId = consort.id;
    dynasty.heirId = heir.id;
    dynasty.regency = false;
    this.recordHistory(faction.id, 'coronation', 'Dynasty Established', `${ruler.name} rules House ${dynasty.house}.`, [ruler.id, heir.id, consort.id]);
  }

  private ensureEstates(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const seed = this.hash(`${factionId}:estates`);
    ESTATES.forEach((definition, index) => {
      const key = this.estateKey(factionId, definition.id);
      campaign.courtEstates[key] ??= {
        id: definition.id,
        factionId,
        name: definition.name,
        influence: 32 + ((seed >> index) % 25),
        approval: 48 + ((seed >> (index + 2)) % 25),
        power: 25 + ((seed >> (index + 4)) % 28),
        privileges: [],
      };
    });
  }

  private syncOfficeHolders() {
    const campaign = GameState.instance.requireCampaign();
    for (const commander of Object.values(campaign.commanders)) {
      if (commander.characterId && campaign.characters[commander.characterId]) continue;
      const character = this.createOfficeCharacter(commander.factionId, 'general', commander.name, commander.loyalty, `portrait_${commander.factionId}_general`);
      character.commanderId = commander.id;
      campaign.characters[character.id] = character;
      commander.characterId = character.id;
    }
    for (const admiral of Object.values(campaign.admirals)) {
      if (admiral.characterId && campaign.characters[admiral.characterId]) continue;
      const character = this.createOfficeCharacter(admiral.factionId, 'admiral', admiral.name, admiral.loyalty, `portrait_${admiral.factionId}_admiral`);
      character.admiralId = admiral.id;
      campaign.characters[character.id] = character;
      admiral.characterId = character.id;
    }
    for (const faction of Object.values(campaign.factions)) {
      for (const advisor of DataRegistry.instance.advisorList().filter((entry) => entry.faction === faction.id)) {
        const id = `character_advisor_${advisor.id}`;
        if (campaign.characters[id]) continue;
        const character = this.createOfficeCharacter(faction.id, 'advisor', advisor.name, 62 + advisor.skill * 3, advisor.portraitAsset ?? `portrait_${faction.id}_chancellor`, id);
        character.advisorRole = advisor.role;
        character.traits = [...new Set([advisor.trait?.toLowerCase() ?? 'administrator', ...character.traits])].slice(0, 3);
        campaign.characters[id] = character;
      }
    }
  }

  private createCharacter(factionId: string, role: CharacterRole, gender: CharacterGender, age: number, health: number, loyalty: number, influence: number, claimStrength: number, salt: number): CharacterState {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const seed = this.hash(`${factionId}:${role}:${campaign.year}:${salt}`);
    const id = `character_${factionId}_${role}_${campaign.turn}_${salt}`;
    const portraitRole = role === 'child' ? 'heir' : role === 'claimant' ? 'heir' : role;
    return {
      id,
      factionId,
      house: faction.dynasty?.house ?? `${factionId} Court`,
      name: this.characterName(factionId, gender, seed),
      gender,
      birthYear: campaign.year - age,
      age,
      health,
      fertility: Math.max(5, 80 - Math.abs(age - 27) * 2 + (seed % 13)),
      alive: true,
      role,
      portraitAsset: `portrait_${factionId}_${this.portraitRole(portraitRole)}`,
      portraitMood: 'neutral' as const,
      traits: this.traitsFor(seed),
      relationships: {},
      parentIds: [],
      childIds: [],
      loyalty,
      influence,
      claimStrength,
    };
  }

  private createOfficeCharacter(factionId: string, role: CharacterRole, name: string, loyalty: number, portraitAsset: string, fixedId?: string): CharacterState {
    const campaign = GameState.instance.requireCampaign();
    const seed = this.hash(`${factionId}:${role}:${name}`);
    return {
      id: fixedId ?? `character_${factionId}_${role}_${seed}`,
      factionId,
      house: campaign.factions[factionId].dynasty?.house ?? `${factionId} Court`,
      name,
      gender: seed % 5 === 0 ? 'female' as const : 'male' as const,
      birthYear: campaign.year - (28 + seed % 27),
      age: 28 + seed % 27,
      health: 62 + seed % 31,
      fertility: 30 + seed % 45,
      alive: true,
      role,
      portraitAsset,
      portraitMood: 'neutral' as const,
      traits: this.traitsFor(seed),
      relationships: {},
      parentIds: [],
      childIds: [],
      loyalty: Math.max(0, Math.min(100, loyalty)),
      influence: 30 + seed % 45,
      claimStrength: 0,
    };
  }

  private considerBirth(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const dynasty = campaign.factions[factionId].dynasty!;
    const ruler = dynasty.rulerId ? campaign.characters[dynasty.rulerId] : undefined;
    const consort = dynasty.consortId ? campaign.characters[dynasty.consortId] : undefined;
    if (!ruler?.alive || !consort?.alive || ruler.childIds.length >= 5 || consort.age < 16 || consort.age > 45) return;
    const chance = Math.min(0.42, Math.max(0.05, (ruler.fertility + consort.fertility) / 420));
    if (this.random(this.hash(`${campaign.turn}:${factionId}:birth`)) > chance) return;
    const gender: CharacterGender = this.hash(`${campaign.turn}:${factionId}:gender`) % 2 ? 'female' : 'male';
    const child = this.createCharacter(factionId, 'child', gender, 0, 92, 70, 5, 35, ruler.childIds.length + 9);
    child.parentIds = [ruler.id, consort.id];
    ruler.childIds.push(child.id); consort.childIds.push(child.id);
    child.relationships[ruler.id] = 45; child.relationships[consort.id] = 50;
    campaign.characters[child.id] = child;
    this.recordHistory(factionId, 'birth', 'A Child of the Dynasty', `${child.name} was born to House ${dynasty.house}.`, [child.id, ruler.id, consort.id]);
    this.addIncident(factionId, 'birth', 'A Dynastic Birth', `${child.name} has been born, strengthening the future of the house.`, 'event_court_birth', [child.id], 1);
    this.recalculateHeir(factionId);
  }

  private killCharacter(characterId: string, cause: string) {
    const campaign = GameState.instance.requireCampaign();
    const character = campaign.characters[characterId];
    if (!character?.alive) return;
    character.alive = false;
    character.health = 0;
    character.deathYear = campaign.year;
    character.deathCause = cause;
    character.portraitMood = 'mourning';
    const faction = campaign.factions[character.factionId];
    this.recordHistory(character.factionId, 'funeral', `${character.name} Died`, `${character.name} died ${cause}.`, [character.id]);
    this.addIncident(character.factionId, 'funeral', 'Court in Mourning', `${character.name} has died ${cause}.`, 'event_court_funeral', [character.id], character.role === 'ruler' ? 5 : 2);
    if (faction.dynasty?.rulerId === character.id) this.resolveSuccession(character.factionId);
    else if (faction.dynasty?.heirId === character.id) this.recalculateHeir(character.factionId);
    if (faction.dynasty?.consortId === character.id) faction.dynasty.consortId = undefined;
    if (faction.dynasty?.regentId === character.id) faction.dynasty.regentId = undefined;
  }

  private resolveSuccession(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const dynasty = faction.dynasty!;
    const successor = this.chooseHeir(factionId);
    if (!successor) {
      faction.legitimacy = Math.max(0, faction.legitimacy - 35);
      dynasty.successionClarity = 0;
      this.addIncident(factionId, 'succession_crisis', 'The Throne Stands Empty', 'No accepted heir remains. Rival houses prepare to seize power.', 'event_court_succession_crisis', [], 5);
      return;
    }
    const previousRuler = dynasty.rulerId ? campaign.characters[dynasty.rulerId] : undefined;
    if (previousRuler) previousRuler.role = 'courtier';
    successor.role = 'ruler';
    successor.title = 'Ruler';
    successor.portraitAsset = `portrait_${factionId}_ruler`;
    dynasty.rulerId = successor.id;
    dynasty.lastSuccessionTurn = campaign.turn;
    dynasty.successionClarity = Math.max(25, Math.min(100, dynasty.successionClarity - 12 + Math.round(successor.claimStrength / 8)));
    faction.legitimacy = Math.max(15, Math.round((faction.legitimacy + successor.claimStrength) / 2));
    this.recalculateHeir(factionId);
    if (successor.age < 16) {
      dynasty.regency = true;
      const regent = this.bestRegent(factionId, successor.id);
      dynasty.regentId = regent?.id;
      if (regent) regent.role = 'regent';
      this.addIncident(factionId, 'regency', 'A Regency Begins', `${successor.name} inherits as a minor. ${regent?.name ?? 'The council'} will govern in the ruler's name.`, 'event_court_regency', [successor.id, ...(regent ? [regent.id] : [])], 4);
    } else {
      dynasty.regency = false;
      dynasty.regentId = undefined;
      this.addIncident(factionId, 'coronation', 'A New Reign', `${successor.name} has ascended the throne of House ${dynasty.house}.`, 'event_court_coronation', [successor.id], 3);
    }
    this.recordHistory(factionId, 'coronation', 'Succession', `${successor.name} succeeded to the throne.`, [successor.id]);
  }

  private recalculateHeir(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const heir = this.chooseHeir(factionId, faction.dynasty?.rulerId);
    for (const character of this.familyForFaction(factionId)) if (character.role === 'heir') character.role = 'child';
    faction.dynasty!.heirId = heir?.id;
    if (heir) { heir.role = 'heir'; heir.portraitAsset = `portrait_${factionId}_heir`; }
  }

  private chooseHeir(factionId: string, excludeId?: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const law = faction.dynasty?.successionLaw ?? 'absolute_primogeniture';
    let candidates = this.familyForFaction(factionId).filter((character) => character.alive && character.id !== excludeId && character.role !== 'consort');
    if (law === 'agnatic_primogeniture') candidates = candidates.filter((character) => character.gender === 'male');
    if (law === 'designated_heir' && faction.dynasty?.heirId) {
      const designated = campaign.characters[faction.dynasty.heirId];
      if (designated?.alive && designated.id !== excludeId) return designated;
    }
    if (law === 'seniority') return candidates.sort((a, b) => b.age - a.age || b.claimStrength - a.claimStrength)[0];
    if (law === 'elective_court') return candidates.sort((a, b) => (b.influence + b.loyalty + b.claimStrength) - (a.influence + a.loyalty + a.claimStrength))[0];
    return candidates.sort((a, b) => b.claimStrength - a.claimStrength || a.birthYear - b.birthYear)[0];
  }

  private bestRegent(factionId: string, excludeId: string) {
    return this.courtiersForFaction(factionId)
      .filter((character) => character.id !== excludeId && character.age >= 20)
      .sort((a, b) => (b.loyalty + b.influence + b.health) - (a.loyalty + a.influence + a.health))[0];
  }

  private processRegency(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const dynasty = faction.dynasty!;
    const ruler = dynasty.rulerId ? campaign.characters[dynasty.rulerId] : undefined;
    if (!dynasty.regency || !ruler) return;
    if (ruler.age >= 16) {
      dynasty.regency = false;
      if (dynasty.regentId && campaign.characters[dynasty.regentId]?.alive) campaign.characters[dynasty.regentId].role = 'courtier';
      dynasty.regentId = undefined;
      faction.legitimacy = Math.min(100, faction.legitimacy + 6);
      this.addIncident(factionId, 'coronation', 'The Ruler Comes of Age', `${ruler.name} has assumed full authority.`, 'event_court_coronation', [ruler.id], 2);
    } else {
      const regent = dynasty.regentId ? campaign.characters[dynasty.regentId] : undefined;
      faction.legitimacy = Math.max(0, faction.legitimacy - (regent && regent.loyalty > 60 ? 0.15 : 0.55));
      if (regent && regent.loyalty < 35 && this.random(this.hash(`${campaign.turn}:${regent.id}:coup`)) < 0.08) {
        this.addIncident(factionId, 'coup', 'The Regent Reaches for the Crown', `${regent.name} has gathered supporters and challenged the young ruler.`, 'event_court_coup', [ruler.id, regent.id], 5);
      }
    }
  }

  private processEstates(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    for (const estate of this.estatesForFaction(factionId)) {
      const target = this.estateApprovalTarget(estate.id, faction);
      estate.approval = Math.max(0, Math.min(100, estate.approval + Math.sign(target - estate.approval) * 0.35));
      estate.power = Math.max(5, Math.min(100, estate.power + (estate.influence - 50) * 0.006));
      if (!estate.activeDemandId && campaign.turn % 10 === this.hash(`${factionId}:${estate.id}`) % 10 && estate.influence >= 35) this.createDemand(factionId, estate);
    }
  }

  private createDemand(factionId: string, estate: CourtEstateState) {
    const campaign = GameState.instance.requireCampaign();
    const templates: Record<CourtEstateId, [string, string, number, number]> = {
      nobility: ['Confirm Noble Privileges', 'The great houses ask the ruler to confirm their tax exemptions and local authority.', 55, 6],
      clergy: ['Endow the Sacred Foundations', 'Religious leaders request funds for temples, mosques, monasteries and schools.', 45, 4],
      merchants: ['Protect the Trade Guilds', 'The guilds seek guaranteed contracts and protection from arbitrary tolls.', 60, 3],
      military: ['Reward the Retainers', 'Commanders demand arrears, honors and estates for their service.', 70, 5],
      commoners: ['Remit the Harvest Tax', 'Village elders petition for relief after difficult seasons.', 40, 2],
    };
    const [title, description, cost, legitimacyCost] = templates[estate.id];
    const id = `demand_${factionId}_${estate.id}_${campaign.turn}`;
    const demand: EstateDemandState = { id, estateId: estate.id, title, description, dueTurn: campaign.turn + 6, rewardApproval: 14, penaltyApproval: 18, treasuryCost: cost, legitimacyCost };
    campaign.estateDemands[id] = demand;
    estate.activeDemandId = id;
    this.addIncident(factionId, 'estate_demand', title, description, 'event_court_estate_demand', [], 2);
  }

  private processExpiredDemands(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    for (const demand of this.demandsForFaction(factionId)) if (demand.dueTurn < campaign.turn) this.respondToDemand(factionId, demand.id, false);
  }

  private considerCourtCrisis(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const dangerous = this.estatesForFaction(factionId).filter((estate) => estate.approval < 22 && estate.power > 55);
    if (!dangerous.length || Object.values(campaign.courtIncidents).some((incident) => incident.factionId === factionId && !incident.resolved && ['coup', 'rebellion'].includes(incident.type))) return;
    if (this.random(this.hash(`${campaign.turn}:${factionId}:crisis`)) > 0.055 + dangerous.length * 0.018) return;
    const estate = dangerous.sort((a, b) => b.power - a.power)[0];
    const type = estate.id === 'military' || estate.id === 'nobility' ? 'coup' : 'rebellion';
    const title = type === 'coup' ? 'A Palace Coup' : 'Estate Rebellion';
    const description = type === 'coup' ? `${estate.name} have occupied the palace approaches and demand a new government.` : `${estate.name} have raised local resistance against the crown.`;
    this.addIncident(factionId, type, title, description, type === 'coup' ? 'event_court_coup' : 'event_court_rebellion', [], 5);
    faction.legitimacy = Math.max(0, faction.legitimacy - 10);
    faction.warExhaustion = Math.min(100, faction.warExhaustion + 6);
    for (const province of Object.values(campaign.provinces).filter((province) => province.owner === factionId).sort((a, b) => b.unrest - a.unrest).slice(0, 2)) province.unrest = Math.min(100, province.unrest + 18);
  }

  private addIncident(factionId: string, type: CourtIncidentState['type'], title: string, description: string, imageAsset: string, characterIds: string[], severity: number) {
    const campaign = GameState.instance.requireCampaign();
    const id = `court_${type}_${factionId}_${campaign.turn}_${Object.keys(campaign.courtIncidents).length}`;
    campaign.courtIncidents[id] = { id, factionId, type, title, description, imageAsset, characterIds, createdTurn: campaign.turn, expiresTurn: campaign.turn + 12, severity, resolved: false };
  }

  private recordHistory(factionId: string, type: CourtIncidentState['type'], title: string, description: string, characterIds: string[]) {
    const campaign = GameState.instance.requireCampaign();
    campaign.dynastyHistory.unshift({ id: `history_${campaign.turn}_${factionId}_${campaign.dynastyHistory.length}`, factionId, year: campaign.year, month: campaign.month, type, title, description, characterIds });
    campaign.dynastyHistory = campaign.dynastyHistory.slice(0, 120);
  }

  private estateApprovalTarget(id: CourtEstateId, faction: FactionState) {
    const war = faction.atWarWith.length > 0;
    if (id === 'military') return 45 + faction.militaryTradition * 0.3 + (war ? 12 : -4);
    if (id === 'merchants') return 48 + faction.treaties.trade.length * 4 - faction.warExhaustion * 0.2;
    if (id === 'nobility') return 42 + faction.legitimacy * 0.35;
    if (id === 'clergy') return 45 + (faction.activeLaws.some((law) => law.includes('temple') || law.includes('faith')) ? 12 : 0);
    return 52 - faction.warExhaustion * 0.35;
  }

  private healthDrift(character: CharacterState) {
    const agePenalty = character.age > 65 ? -3 : character.age > 50 ? -1 : character.age < 18 ? 1 : 0;
    const traitBonus = character.traits.includes('cautious') ? 1 : character.traits.includes('brave') ? -0.5 : 0;
    return agePenalty + traitBonus;
  }

  private shouldDie(character: CharacterState, turn: number) {
    if (character.health <= 0) return true;
    const ageRisk = character.age < 45 ? 0.003 : character.age < 60 ? 0.012 : character.age < 75 ? 0.055 : 0.16;
    const healthRisk = Math.max(0, (45 - character.health) / 220);
    return this.random(this.hash(`${turn}:${character.id}:mortality`)) < ageRisk + healthRisk;
  }

  private deathCause(character: CharacterState) {
    if (character.health < 25) return 'after a long illness';
    if (character.age > 72) return 'of old age';
    return ['after a sudden fever', 'following a brief illness', 'unexpectedly at court'][this.hash(character.id) % 3];
  }

  private characterName(factionId: string, gender: CharacterGender, seed: number) {
    const names = GIVEN_NAMES[factionId] ?? ['Arya', 'Raden', 'Sari', 'Dewi', 'Prabu', 'Ratna'];
    const given = names[seed % names.length];
    const house = GameState.instance.requireCampaign().factions[factionId].dynasty?.house.split(' ')[0] ?? factionId;
    const honorific = gender === 'female'
      ? (['portuguese', 'dutch_voc', 'british_eic'].includes(factionId) ? 'Lady' : factionId === 'ming' ? 'Lady' : 'Ratu')
      : (['portuguese', 'dutch_voc', 'british_eic'].includes(factionId) ? 'Lord' : factionId === 'ming' ? 'Prince' : 'Raden');
    return `${honorific} ${given} ${house}`;
  }

  private traitsFor(seed: number) {
    return [TRAITS[seed % TRAITS.length], TRAITS[(seed >> 3) % TRAITS.length], TRAITS[(seed >> 6) % TRAITS.length]].filter((value, index, values) => values.indexOf(value) === index);
  }

  private portraitRole(role: CharacterRole) {
    if (role === 'ruler' || role === 'heir' || role === 'consort' || role === 'general' || role === 'admiral' || role === 'governor') return role;
    if (role === 'regent') return 'chancellor';
    if (role === 'advisor') return 'chancellor';
    return 'heir';
  }

  private roleOrder(role: CharacterRole) {
    return ['ruler', 'heir', 'consort', 'regent', 'child', 'claimant', 'governor', 'general', 'admiral', 'advisor', 'courtier'].indexOf(role);
  }

  private estateKey(factionId: string, estateId: CourtEstateId) { return `${factionId}:${estateId}`; }
  private hash(value: string) { let hash = 2166136261; for (const character of value) { hash ^= character.charCodeAt(0); hash = Math.imul(hash, 16777619); } return hash >>> 0; }
  private random(seed: number) { return ((Math.imul(seed ^ (seed >>> 15), 2246822519) ^ Math.imul(seed ^ (seed >>> 13), 3266489917)) >>> 0) / 4294967295; }
}
